// src/views/auth/SignUp.js
// Sign Up Page Component - Updated UI to match SignIn

import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
// Chakra imports
import {
    Box, Button, Flex, FormControl, FormLabel, Heading, Icon, Input,
    InputGroup, InputRightElement, Text, useColorModeValue, Select,
    VStack, useToast, Image, // Added Image
} from "@chakra-ui/react";
// Custom components
import { HSeparator } from "../../components/separator/Separator";
// Removed DefaultAuth import
// Assets
import illustration from "../../assets/img/auth/auth.png"; // Ensure path is correct
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";

// --- Import Config ---
import { config } from "../../config"; // Import config object

// Define allowed roles - Including 'tech'
const ALLOWED_ROLES = ['admin', 'staff', 'user', 'tech'];

function SignUp() {
    // --- Color Mode Values (Match SignIn) ---
    const textColor = useColorModeValue("gray.800", "white");
    const textColorSecondary = useColorModeValue("gray.500", "gray.400");
    const textColorBrand = useColorModeValue("blue.500", "blue.300");
    const authBg = useColorModeValue("white", "gray.800");
    const brandStars = useColorModeValue("brand.500", "brand.400"); // Keep if used elsewhere
    const textColorDetails = useColorModeValue("gray.600", "gray.400");

    // --- State ---
    const [show, setShow] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const handleClick = () => setShow(!show);
    const handleConfirmClick = () => setShowConfirm(!showConfirm);
    const [formData, setFormData] = useState({ username: '', email: '', password: '', confirmPassword: '', role: 'user' }); // Default role to 'user' or 'tech'?
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();

    // --- Handlers ---
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);

        if (formData.password !== formData.confirmPassword) {
            toast({ title: "Error", description: "Passwords do not match.", status: "error", duration: 5000, isClosable: true });
            setIsLoading(false); return;
        }
        if (formData.password.length < 6) {
            toast({ title: "Error", description: "Password must be at least 6 characters long.", status: "error", duration: 5000, isClosable: true });
            setIsLoading(false); return;
        }

        // Exclude confirmPassword from the data sent to the backend
        const { confirmPassword, ...registrationData } = formData;
        console.log("Attempting registration to:", `${config.apiUrl}/auth/register`);

        try {
            const response = await fetch(`${config.apiUrl}/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(registrationData)
            });
            const data = await response.json();
            if (!response.ok) { throw new Error(data.message || (data.errors ? data.errors.map(e => e.msg).join(', ') : `Registration failed: ${response.status}`)); }

            console.log("Registration successful:", data.user);
            toast({ title: "Account Created", description: "Registration successful! Please sign in.", status: "success", duration: 5000, isClosable: true });
            navigate('/auth/sign-in'); // Redirect to sign-in page
        } catch (error) {
            console.error("Registration API call failed:", error);
            toast({ title: "Registration Failed", description: error.message || "An error occurred.", status: "error", duration: 5000, isClosable: true });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        // Main Flex container for the dual-screen layout
        <Flex position="relative" h="110vh" w="110%">
            {/* Left Side: Form Container */}
            <Flex
                direction="column"
                w={{ base: "100%", md: "50%", lg: "45%" }} // Adjusted width
                px={{ base: 6, md: 16 }}
                py={{ base: 10, md: 20 }}
                justifyContent="center"
                alignItems="center" // Center content vertically
                bg={authBg}
            >
                <Box w="100%" maxW="420px">
                    <Heading color={textColor} fontSize="3xl" mb={3} textAlign="left">
                        Sign Up
                    </Heading>
                    <Text fontSize="md" color={textColorSecondary} mb={8} textAlign="left">
                        Enter your details to create your account!
                    </Text>

                    {/* Sign Up Form */}
                    <form onSubmit={handleSubmit}>
                        <VStack spacing={5} align="stretch">
                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="medium" color={textColor}>Username</FormLabel>
                                <Input
                                    name="username" type="text" placeholder="Enter your username" size="lg"
                                    borderRadius="md" value={formData.username} onChange={handleInputChange}
                                    bg={useColorModeValue('gray.50', 'gray.700')}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="medium" color={textColor}>Email</FormLabel>
                                <Input
                                    name="email" type="email" placeholder="mail@example.com" size="lg"
                                    borderRadius="md" value={formData.email} onChange={handleInputChange}
                                    bg={useColorModeValue('gray.50', 'gray.700')}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="medium" color={textColor}>Password</FormLabel>
                                <InputGroup size="lg">
                                    <Input
                                        name="password" type={show ? "text" : "password"} placeholder="Min. 6 characters"
                                        borderRadius="md" value={formData.password} onChange={handleInputChange}
                                        bg={useColorModeValue('gray.50', 'gray.700')}
                                    />
                                    <InputRightElement>
                                        <Icon as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye} onClick={handleClick} _hover={{ cursor: "pointer" }} color="gray.400" />
                                    </InputRightElement>
                                </InputGroup>
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="medium" color={textColor}>Confirm Password</FormLabel>
                                <InputGroup size="lg">
                                    <Input
                                        name="confirmPassword" type={showConfirm ? "text" : "password"} placeholder="Confirm your password"
                                        borderRadius="md" value={formData.confirmPassword} onChange={handleInputChange}
                                        bg={useColorModeValue('gray.50', 'gray.700')}
                                    />
                                    <InputRightElement>
                                        <Icon as={showConfirm ? RiEyeCloseLine : MdOutlineRemoveRedEye} onClick={handleConfirmClick} _hover={{ cursor: "pointer" }} color="gray.400" />
                                    </InputRightElement>
                                </InputGroup>
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel fontSize="sm" fontWeight="medium" color={textColor}>Role</FormLabel>
                                <Select
                                    id="role" name="role" value={formData.role} onChange={handleInputChange}
                                    size="lg" borderRadius="md" bg={useColorModeValue('gray.50', 'gray.700')}
                                >
                                    {/* Include 'tech' role */}
                                    {ALLOWED_ROLES.map(role => (<option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>))}
                                </Select>
                            </FormControl>

                            <Button
                                w="100%" h="50px" type="submit" colorScheme="blue"
                                borderRadius="md" isLoading={isLoading} fontSize="md" fontWeight="bold" mt={4} // Added top margin
                            >
                                Create Account
                            </Button>
                        </VStack>
                    </form>

                    <Text mt={6} fontSize="sm" color={textColorSecondary} textAlign="center">
                        Already have an account?{" "}
                        <NavLink to="/auth/sign-in">
                            <Text as="span" color={textColorBrand} fontWeight="medium"> Sign In </Text>
                        </NavLink>
                    </Text>
                </Box>
            </Flex>

            {/* Right Side Illustration */}
            <Flex
                w={{ base: "0%", md: "35%", lg: "60%" }} // Adjusted width
                display={{ base: "none", md: "flex" }}
                align="center"
                justify="center"
                bgImage={`url(${illustration})`}
                bgSize="cover"
                bgPosition="center"
                borderBottomLeftRadius={{ lg: "120px", xl: "200px" }}
            />
        </Flex>
    );
}

export default SignUp;
